import apiClient from '../config/axiosConfig';
import { API_BASE_URL } from '../utils/apiUtils';
import { sendFollowUpEmail } from './emailApi';

const validateLeadId = (leadId) => {
    if (!leadId || typeof leadId !== 'string' || leadId.trim() === '') {
        throw new Error('Lead ID is required and must be a valid string');
    }
    return leadId.trim();
};

const validateUserId = (userId) => {
    if (!userId || typeof userId !== 'string' || userId.trim() === '') {
        throw new Error('User ID is required and must be a valid string');
    }
    return userId.trim();
};

const validateToken = (token) => {
    if (token && typeof token !== 'string') {
        throw new Error('Authentication token must be a string if provided');
    }
    return token ? token.trim() : null;
};

const validateLeadData = (leadData) => {
    if (!leadData || typeof leadData !== 'object') {
        throw new Error('Lead data must be a valid object');
    }

    const validatedData = { ...leadData };

    if (validatedData.leadName !== undefined && validatedData.leadName !== null) {
        if (typeof validatedData.leadName !== 'string' || validatedData.leadName.trim() === '') {
            throw new Error('Lead name must be a valid non-empty string if provided');
        }
        validatedData.leadName = validatedData.leadName.trim();
    }

    if (!validatedData.leadEmail || typeof validatedData.leadEmail !== 'string' || validatedData.leadEmail.trim() === '') {
        throw new Error('Lead email is required');
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(validatedData.leadEmail.trim())) {
        throw new Error('Lead email must be in a valid format');
    }

    validatedData.leadEmail = validatedData.leadEmail.trim().toLowerCase();

    if (validatedData.leadPhoneNumber) {
        validatedData.leadPhoneNumber = String(validatedData.leadPhoneNumber).trim();
        if (validatedData.leadPhoneNumber === '') {
            delete validatedData.leadPhoneNumber;
        }
    }

    if (validatedData.leadCompany) {
        if (typeof validatedData.leadCompany !== 'string') {
            throw new Error('Lead company must be a string');
        }
        validatedData.leadCompany = validatedData.leadCompany.trim();
    }

    if (validatedData.leadSource) {
        if (typeof validatedData.leadSource !== 'string') {
            throw new Error('Lead source must be a string');
        }
        validatedData.leadSource = validatedData.leadSource.trim();
    } else {
        validatedData.leadSource = 'manually-added';
    }

    if (validatedData.leadNotes) {
        if (typeof validatedData.leadNotes !== 'string') {
            throw new Error('Lead notes must be a string');
        }
        validatedData.leadNotes = validatedData.leadNotes.trim();
    }

    return validatedData;
};

const validateCardData = (cardData) => {
    if (!cardData || typeof cardData !== 'object') {
        throw new Error('Card data must be a valid object');
    }

    return {
        ...cardData,
        name: cardData.name ? validateStringValue(cardData.name, 'Card name') : undefined,
        email: cardData.email ? validateEmail(cardData.email) : undefined,
        phone: cardData.phone ? validateStringValue(cardData.phone, 'Card phone') : undefined
    };
};

const validateEmail = (email) => {
    if (!email || typeof email !== 'string' || email.trim() === '') {
        throw new Error('Email must be a valid string');
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email.trim())) {
        throw new Error('Email must be in a valid format');
    }

    return email.trim().toLowerCase();
};

const validateStringValue = (value, fieldName) => {
    if (value !== null && value !== undefined && typeof value !== 'string') {
        throw new Error(`${fieldName} must be a string`);
    }
    return value?.trim();
};

const validateProfilePictureUrl = (profilePictureUrl) => {
    if (!profilePictureUrl || typeof profilePictureUrl !== 'string' || profilePictureUrl.trim() === '') {
        throw new Error('Profile picture URL is required and must be a valid string');
    }

    try {
        new URL(profilePictureUrl.trim());
        return profilePictureUrl.trim();
    } catch (error) {
        throw new Error('Profile picture URL must be a valid URL');
    }
};

const handleLeadCaptureAuthError = (error, operation = 'API operation') => {
    if (error.response?.status === 401) {
        localStorage.removeItem('authToken');
        localStorage.removeItem('authUser');
        throw new Error('Your session has expired. Please log in again');
    }

    if (error.response?.status === 403) {
        throw new Error('You do not have permission to access lead data');
    }

    if (error.response?.status === 404) {
        throw new Error('Lead not found or no longer available');
    }

    if (error.response?.status === 409) {
        throw new Error('Lead already exists or conflicts with existing data');
    }

    if (error.response?.status === 422) {
        const validationErrors = error.response?.data?.errors || [];
        const errorMessage = validationErrors.length > 0
            ? `Validation errors: ${validationErrors.join(', ')}`
            : 'Invalid lead data provided';
        throw new Error(errorMessage);
    }

    if (error.response?.status >= 500) {
        throw new Error('Lead service error. Please try again later.');
    }

    if (error.code === 'ERR_NETWORK') {
        throw new Error('Network error. Please check your connection and try again.');
    }

    if (error.code === 'ECONNABORTED') {
        throw new Error('Request timeout. Please try again.');
    }

    const errorMessage = error.response?.data?.message || error.message || 'An unexpected error occurred';
    throw new Error(errorMessage);
};

export const createLead = async (leadData, cardData, token = null) => {
    try {
        const validLeadData = validateLeadData(leadData);
        const validCardData = validateCardData(cardData);
        const validToken = validateToken(token);

        const requestConfig = {
            method: 'POST',
            url: `${API_BASE_URL}/api/lead`,
            data: validLeadData,
            headers: {
                'Content-Type': 'application/json'
            }
        };

        if (validToken) {
            requestConfig.headers.Authorization = `Bearer ${validToken}`;
        }

        const response = await apiClient(requestConfig);

        if (response.data.success && validToken) {
            try {
                await sendFollowUpEmail(response.data.lead, validCardData, validToken);
            } catch (emailError) {
                // Don't fail the entire operation if email fails
            }
        }

        const mongoDBId = response.data.lead._id;

        return mongoDBId;
    } catch (error) {
        handleLeadCaptureAuthError(error, 'Creating lead');
    }
};

export const saveLead = async (leadData, token = null) => {
    try {
        const validLeadData = validateLeadData(leadData);
        const validToken = validateToken(token);

        const requestConfig = {
            method: 'POST',
            url: `${API_BASE_URL}/api/lead`,
            data: validLeadData,
            headers: {
                'Content-Type': 'application/json'
            }
        };

        if (validToken) {
            requestConfig.headers.Authorization = `Bearer ${validToken}`;
        }

        const response = await apiClient(requestConfig);

        return {
            success: true,
            lead: response.data.lead,
            message: response.data.message || 'Lead created successfully'
        };
    } catch (error) {
        handleLeadCaptureAuthError(error, 'Saving lead');
    }
};

export const fetchLeadById = async (leadId) => {
    try {
        const validLeadId = validateLeadId(leadId);

        const response = await apiClient.get(`${API_BASE_URL}/api/lead/${validLeadId}`);

        return response.data;
    } catch (error) {
        handleLeadCaptureAuthError(error, 'Fetching lead by ID');
    }
};

export const getLeadsByUser = async (userId) => {
    try {
        const validUserId = validateUserId(userId);

        const response = await apiClient.get(`${API_BASE_URL}/api/lead/user/${validUserId}`);

        return response.data.leads || [];
    } catch (error) {
        if (error.response && error.response.status === 404) {
            return [];
        }
        handleLeadCaptureAuthError(error, 'Fetching leads by user');
    }
};

export const getAllLeads = async () => {
    try {
        const response = await apiClient.get(`${API_BASE_URL}/api/lead`);

        return response.data.leads;
    } catch (error) {
        handleLeadCaptureAuthError(error, 'Fetching all leads');
    }
};

export const updateLead = async (leadId, leadData) => {
    try {
        const validLeadId = validateLeadId(leadId);
        const validLeadData = validateLeadData(leadData);

        const response = await apiClient.put(`${API_BASE_URL}/api/lead/${validLeadId}`, validLeadData);

        return response.data;
    } catch (error) {
        handleLeadCaptureAuthError(error, 'Updating lead');
    }
};

export const deleteLead = async (leadId) => {
    try {
        const validLeadId = validateLeadId(leadId);

        const response = await apiClient.delete(`${API_BASE_URL}/api/lead/${validLeadId}`);

        return response.data;
    } catch (error) {
        handleLeadCaptureAuthError(error, 'Deleting lead');
    }
};

export const updateLeadProfilePicture = async (leadId, profilePictureUrl) => {
    try {
        const validLeadId = validateLeadId(leadId);
        const validProfilePictureUrl = validateProfilePictureUrl(profilePictureUrl);

        const response = await apiClient.put(`${API_BASE_URL}/api/lead/update-profile-picture/${validLeadId}`, {
            profilePictureUrl: validProfilePictureUrl
        });

        if (response.status === 200) {
            return response.data;
        } else {
            throw new Error('Failed to update profile picture with an unexpected status code');
        }
    } catch (error) {
        handleLeadCaptureAuthError(error, 'Updating lead profile picture');
    }
};